import { useEffect, useState } from 'react'
import {
  ApiError, addPenTreatment, createFishGroup, fishState, harvestFish,
  moveFish, withdrawMove,
  type FishState, type Pen,
} from '../api'
import { Flyttark, type Maal, type Svar } from './Flyttark'
import { dagMnd } from './Varsel'
import { useT } from '../lang'

/* Which fish are where, where they went, and the three places that are not a
 * ring.
 *
 * THE EMPTY STATE IS NOT A FORM.
 *
 * The register already knows: settefisk from Strømsnes, carried by MOWI STAR,
 * delivered 7 July 2025. So the first thing a farm sees is that sentence and a
 * yes — one tap, nothing typed, and the group carries `register_segment_id`
 * back to where it came from. A product that opens by asking for homework you
 * have already handed in somewhere else is a product people put off.
 *
 * WHAT THE REGISTER CANNOT DO, and why this exists at all: it counts transfers
 * between LOCALITIES and has no merd axis. Which ring the fish stand in is
 * known only to the crew on the walkway — the same blindness that made per-merd
 * counting worth building, one level down.
 *
 * THE TRAY IS THREE DESTINATIONS THAT ARE NOT MERDER.
 *
 * Sett inn fisk, avlusing, slakteri. Drag a badge onto one of them, or tap it
 * and then tap a ring. From the walkway "these fish go to merd 5", "these fish
 * go on the boat" and "these fish are being deloused" are the same gesture
 * pointed at different things, so they are one gesture here — and organising
 * them as three separate screens would be organising the product around our
 * tables rather than around their Tuesday.
 *
 * A POPPED-OUT GROUP IS THE ONE STATE THIS MODEL LEAVES OPEN, and the page
 * refuses to be quiet about it: fish standing in no ring get a banner and the
 * next tap on a merd puts them there.
 */

export interface Ark { maal: Maal; from: string | null; to: string }

export function Fisken({
  siteId, pens, canWrite, onChanged, ark, setArk, armFrom, setArmFrom,
  valgtMerd, onPlaced,
}: {
  siteId: string
  pens: Pen[]
  canWrite: boolean
  /** The map redraws its badges from this. */
  onChanged: (state: FishState) => void
  /* THE PROPOSED ACT LIVES IN THE PARENT, not here, because the gesture that
     makes it happens on the MAP and the sheet that resolves it renders here.
     Two components, one act; the state belongs to whoever holds both. */
  ark: Ark | null
  setArk: (v: Ark | null) => void
  /** The ring a move has been armed from by TAPPING a badge. */
  armFrom: string | null
  setArmFrom: (v: string | null) => void
  /** The ring the reader last tapped. Used only while something is waiting to
   *  be placed, when a tap stops meaning "look at this" and starts meaning
   *  "put them here". */
  valgtMerd: string | null
  onPlaced: () => void
}) {
  const t = useT()
  const [state, setState] = useState<FishState | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /** A group standing in no ring, waiting to be told where it went. */
  const [venter, setVenter] = useState<
    { group_id: string; group_name: string } | null>(null)
  /** The log row that is asking. Design system principle 7: destructive
   *  actions ARM IN PLACE, and once armed the label becomes specific -- never
   *  "Confirm" -- with a line saying what will happen and what will not. */
  const [spor, setSpor] = useState<string | null>(null)
  /** The tray item armed for a tap-then-tap, for anyone who cannot drag. */
  const [arm, setArm] = useState<Maal | null>(null)
  /* A SHUFFLE IS SEVERAL MOVES, NOT ONE.
     Emptying a ring for cleaning is "6 into 3, then 5 into 6, then 2 into 5" —
     one job with three steps. Dropping the reader back to the start after each
     one, and churning the record under them while they are still working,
     makes a single job feel like three unrelated ones. So the session stays
     open until they say it is finished, and the log holds still until then.

     Each move is still WRITTEN as it happens. Staging them in the browser and
     committing at the end would read tidier and would lose a whole shuffle to a
     flat battery, which is the one outcome worth designing against. */
  /* THE RECEIPT NAMES THE ACT THAT JUST HAPPENED.
     This was a bare counter and the sentence under it always read "move
     recorded" -- so a delousing, a stocking and a harvest all reported
     themselves as moves. Combined with the harvested-groups line printed
     directly above it, delousing merd 1 answered "H25-AC went to the
     slaughterhouse" and "1 move recorded", neither of which was what had just
     been done. `n` still counts, because a shuffle is genuinely several. */
  const [kvitt, setKvitt] = useState<
    { maal: Maal; merd: string; n: number } | null>(null)

  const adopt = (s: FishState) => { setState(s); onChanged(s) }

  useEffect(() => {
    let dead = false
    void fishState(siteId).then((r) => { if (!dead) adopt(r) })
      // A farm that has not bought the lice product answers 403 here, and that
      // is not an error to apologise for: the panel simply is not there.
      .catch(() => { if (!dead) setState(null) })
    return () => { dead = true }
  }, [siteId])

  /* A tap on a ring, while something is waiting for one. Two meanings for one
     tap, and which it has is decided by whether anything is homeless — which
     is visible on screen, in a banner, so nobody is guessing. */
  useEffect(() => {
    if (!valgtMerd || !canWrite) return
    if (venter) {
      setArk({ maal: 'pen', from: null, to: valgtMerd })
      onPlaced()
    } else if (armFrom && armFrom !== valgtMerd) {
      setArk({ maal: 'pen', from: armFrom, to: valgtMerd })
      setArmFrom(null)
      onPlaced()
    } else if (arm === 'stock') {
      setArk({ maal: 'stock', from: null, to: valgtMerd })
      setArm(null)
      onPlaced()
    }
  }, [valgtMerd])

  if (!state) return null

  const penNo = (id: string) =>
    pens.find((p) => p.pen_id === id)?.pen_no ?? id
  const held = (penId: string) =>
    state.occupancy.find((o) => o.pen_id === penId) ?? null

  const fail = (e: unknown) => setErr(
    e instanceof ApiError ? (e.detail ?? e.code)
      : t('Could not reach the receiver.'))

  const godta = async () => {
    if (!state.suggestion) return
    setBusy(true); setErr(null)
    try {
      const s = state.suggestion
      const r = await createFishGroup(siteId, {
        name: s.name, stocked_on: s.stocked_on, origin: s.origin,
        register_segment_id: s.segment_id,
        // Every ring, because a smolt delivery fills the array. Wrong for a
        // farm that stocked half of it, and fixable in four drags — which
        // beats asking eight questions of the farm where it is right.
        pen_ids: pens.map((p) => p.pen_id),
        // Ours, not theirs, until somebody at the farm corrects it.
        // lice_treatments.source draws the same line.
        source: 'register',
      })
      adopt({ ...state, groups: r.groups, occupancy: r.occupancy,
              suggestion: null })
    } catch (e) { fail(e) } finally { setBusy(false) }
  }

  const utfør = async (v: Svar & { displace: boolean }) => {
    if (!ark) return
    setBusy(true); setErr(null)
    try {
      if (ark.maal === 'stock') {
        const r = await createFishGroup(siteId, {
          name: v.name ?? 'Ny gruppe', stocked_on: v.on, origin: v.origin,
          pen_ids: [ark.to],
        })
        adopt({ ...state, groups: r.groups, occupancy: r.occupancy,
                suggestion: null })
      } else if (ark.maal === 'harvest') {
        const occ = held(ark.to)
        if (!occ) return
        const r = await harvestFish(siteId, {
          group_id: occ.group_id, from_pen_id: ark.to, on: v.on,
          vessel_name: v.vessel_name ?? null, mmsi: v.mmsi ?? null,
        })
        adopt({ ...state, groups: r.groups, occupancy: r.occupancy,
                moves: r.moves })
      } else if (ark.maal === 'treatment') {
        // A delousing does not move fish. It records what was done to the ring
        // they are standing in, which is what §9 asks to be documented.
        await addPenTreatment(siteId, {
          pen_ids: [ark.to], treated_at: v.on,
          method: v.method ?? 'termisk',
        })
      } else {
        const occ = ark.from ? held(ark.from) : null
        const group = occ?.group_id ?? venter?.group_id
        if (!group) return
        const r = await moveFish(siteId, {
          group_id: group,
          // The ring the badge was dragged off, not just the group. Omitted
          // only when placing fish that are standing nowhere.
          ...(ark.from ? { from_pen_id: ark.from } : {}),
          to_pen_id: ark.to, on: v.on, part: v.part, n_fish: v.n_fish,
          reason: v.reason, displace: v.displace,
        })
        adopt({ ...state, groups: r.groups, occupancy: r.occupancy,
                moves: r.moves })
        setVenter(r.displaced)
      }
      if (ark.maal !== 'pen') setVenter(null)
      const merd = penNo(ark.to)
      setKvitt((k) => (k && k.maal === ark.maal
        ? { ...k, merd, n: k.n + 1 }
        : { maal: ark.maal, merd, n: 1 }))
      setArk(null)
    } catch (e) { fail(e) } finally { setBusy(false) }
  }

  const angre = async (occupancyId: string) => {
    setBusy(true); setErr(null)
    try {
      const r = await withdrawMove(siteId, occupancyId)
      adopt({ ...state, groups: r.groups, occupancy: r.occupancy,
              moves: r.moves })
    } catch (e) { fail(e) } finally { setBusy(false) }
  }

  /* NO ICONS. The design system names this exact case as decoration: "an icon
     beside a label that already says the word". Three emoji beside three
     Norwegian nouns are three pictures of what the noun already said, and
     emoji are somebody else's typeface besides. */
  const TRAY: { maal: Maal; label: string }[] = [
    { maal: 'stock', label: t('Put fish in') },
    { maal: 'treatment', label: t('Delousing') },
    { maal: 'harvest', label: t('Slaughterhouse') },
  ]

  /* A tray item is a DESTINATION when a badge is armed, and an ARMING when
     nothing is. Same button, and which it is is on the screen: the badge is
     ringed, and the line under the tray says what the next tap does. */
  const trayTap = (maal: Maal) => {
    if (armFrom) {
      setArk({ maal, from: armFrom, to: armFrom })
      setArmFrom(null)
      return
    }
    setArm(arm === maal ? null : maal)
  }

  return (
    <div className="stack" style={{ gap: 10 }}>
      {err && <div className="notice bad">{err}</div>}

      {/* One sentence and a yes. Everything in it came from the register, so
          the farm is confirming rather than reporting. */}
      {state.suggestion && canWrite && (
        <div className="notice">
          <strong>{t('Is this the fish standing here now?')}</strong>{' '}
          {state.suggestion.origin_kind === 'settefisk'
            ? t('Smolt from') : t('Fish from')}{' '}
          <strong>{state.suggestion.origin ?? '—'}</strong>
          {state.suggestion.vessel && <>{' '}{t('carried by')}{' '}
            {state.suggestion.vessel}</>}
          {state.suggestion.stocked_on && <>{', '}
            {dagMnd(state.suggestion.stocked_on)}{' '}
            {state.suggestion.stocked_on.slice(0, 4)}</>}.
          <div className="row" style={{ gap: 8, marginTop: 10 }}>
            <button className="btn sm" disabled={busy}
              onClick={() => void godta()}>
              {busy ? t('Saving…') : t('Yes, that is them')}
            </button>
          </div>
          <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>
            {t('From the register. You can correct it afterwards — every pen gets the group, and you drag from there.')}
          </div>
        </div>
      )}

      {/* The one open state this model permits, and the page refuses to be
          quiet about it. */}
      {venter && (
        <div className="notice bad">
          <strong>{venter.group_name}</strong>{' '}
          {t('is standing in no pen. Tap the pen it went to.')}
        </div>
      )}

      {/* The three destinations that are not merder. Drag a badge onto one, or
          tap it and then tap a ring — the second is the one that matters in
          gloves. */}
      {canWrite && (
        <div className="fisketray">
          {TRAY.map((x) => (
            <button key={x.maal} type="button"
              data-fishdrop={x.maal}
              className={`traybit${arm === x.maal ? ' on' : ''}`}
              disabled={!!armFrom && x.maal === 'stock'}
              onClick={() => trayTap(x.maal)}>
              {x.label}
            </button>
          ))}
        </div>
      )}
      {/* What the next tap does, said where the next tap is. */}
      {armFrom && (
        <div className="notice">
          <strong>{held(armFrom)?.group_name}</strong>{' '}
          {t('is picked up from pen')} {penNo(armFrom)}.{' '}
          {t('Tap the pen they go to, or one of the three above.')}
          <button className="btn ghost sm" style={{ marginLeft: 8 }}
            onClick={() => setArmFrom(null)}>{t('Cancel')}</button>
        </div>
      )}
      {!armFrom && arm && arm !== 'stock' && (
        <div className="faint" style={{ fontSize: 12 }}>
          {t('Now tap the pen it applies to.')}
        </div>
      )}
      {!armFrom && arm === 'stock' && (
        <div className="faint" style={{ fontSize: 12 }}>
          {t('Now tap the pen they go into.')}
        </div>
      )}

      {ark && (
        <Flyttark maal={ark.maal} siteId={siteId}
          fra={ark.from ? penNo(ark.from) : (ark.maal === 'pen' ? null
            : penNo(ark.to))}
          til={penNo(ark.to)}
          gruppe={(ark.from ? held(ark.from)?.group_name : null)
            ?? venter?.group_name ?? held(ark.to)?.group_name ?? ''}
          opptatt={ark.maal === 'pen' ? (held(ark.to)?.group_name ?? null)
            : null}
          /* Which fish, in which ring. Without both, the boat list is every
             call of the cycle including the ones from before these fish were
             here — and the harvest then refuses them one at a time. */
          gruppeId={held(ark.to)?.group_id ?? null}
          fraPenId={ark.to}
          busy={busy}
          onCancel={() => setArk(null)}
          onGo={(v) => void utfør(v)} />
      )}

      {/* Where a shuffle continues from. Not a toast that fades: the next step
          of the job is a button, and finishing it is a different button. */}
      {kvitt && !ark && !venter && !armFrom && (
        <div className="notice">
          <strong>
            {kvitt.maal === 'treatment'
              ? t('Delousing recorded in pen %s.').replace('%s', kvitt.merd)
              : kvitt.maal === 'harvest'
                ? t('Pen %s emptied to the slaughterhouse.')
                  .replace('%s', kvitt.merd)
                : kvitt.maal === 'stock'
                  ? t('Fish put into pen %s.').replace('%s', kvitt.merd)
                  : `${kvitt.n} ${kvitt.n === 1 ? t('move recorded.')
                    : t('moves recorded.')}`}
          </strong>
          {/* Only after a move. "Tap another fish to move more" is a
              non-sequitur under a delousing, and an instruction that does not
              follow from what you just did reads as the product having lost
              track of what you did. */}
          {kvitt.maal === 'pen' && (
            <div className="faint" style={{ fontSize: 12, marginTop: 4 }}>
              {t('Tap another fish to move more.')}
            </div>
          )}
          <div className="row" style={{ gap: 8, marginTop: 10 }}>
            <button className="btn primary sm" onClick={() => setKvitt(null)}>
              {t('Finish')}
            </button>
          </div>
        </div>
      )}

      {/* A generation that has gone to the slaughterhouse. Said rather than
          allowed to vanish: it left the map when its last ring emptied, and a
          farm that sees nothing where a population was reads that as data
          loss.

          BELOW THE RECEIPT AND NOT ABOVE IT. Printed above, this standing fact
          about a group harvested days ago sat exactly where the answer to
          "what did I just do" goes -- so delousing merd 1 replied "H25-AC went
          to the slaughterhouse". It is history; it belongs with the log. */}
      {state.groups.filter((g) => g.harvested_on).map((g) => (
        <p key={g.group_id} className="faint"
          style={{ margin: 0, fontSize: 12 }}>
          <strong>{g.name}</strong>{' '}{t('went to the slaughterhouse')}{' '}
          {dagMnd(g.harvested_on!)}.
        </p>
      ))}

      {/* The record. Read newest first, because the question people bring to it
          is "when did we last move anything", not "what happened in 2021".

          HELD STILL WHILE A SHUFFLE IS RUNNING. A log that grows a line under
          somebody's thumb between steps of one job turns three steps into three
          events, and they are not. */}
      {!kvitt && state.moves.length > 0 && (
        <details className="flyttlogg">
          <summary>{state.moves.length} {t('movements on record')}</summary>
          <ul>
            {state.moves.map((m) => (
              <li key={m.occupancy_id}>
                <span className="faint">{dagMnd(m.on)}</span>{' '}
                <strong>{m.group_name}</strong>{' '}
                {m.from_pen_id
                  ? <>{t('Pen')} {penNo(m.from_pen_id)} → {t('Pen')} {penNo(m.pen_id)}</>
                  : <><span className="brikke sm">{t('put in')}</span>{' '}
                    {t('Pen')} {penNo(m.pen_id)}</>}
                {m.reason && <span className="faint">
                  {' · '}{t(`why:${m.reason}`)}</span>}
                {canWrite && (
                  <button className={`btn sm${spor === m.occupancy_id
                    ? ' danger' : ' ghost'}`} disabled={busy}
                    style={{ marginLeft: 8 }}
                    onClick={() => {
                      if (spor !== m.occupancy_id) {
                        setSpor(m.occupancy_id)
                        return
                      }
                      setSpor(null)
                      void angre(m.occupancy_id)
                    }}>
                    {spor === m.occupancy_id
                      ? (m.from_pen_id
                        ? `${t('Put them back in pen')} ${penNo(m.from_pen_id)}`
                        : `${t('Leave pen')} ${penNo(m.pen_id)} ${t('empty')}`)
                      : t('Take it back')}
                  </button>
                )}
                {/* WHAT IT WILL DO, said before it does it.
                    A stocking and a move are two different acts and this list
                    drew them as one row with one button. Taking back a MOVE
                    puts the fish where they came from; taking back a STOCKING
                    says the ring has never held anything -- and five taps of a
                    button that looked like an undo emptied five rings that
                    have fish in them. */}
                {spor === m.occupancy_id && (
                  <div className="faint" style={{ fontSize: 12, marginTop: 4 }}>
                    {m.from_pen_id
                      ? <>{t('The move is withdrawn.')}{' '}
                        <strong>{m.group_name}</strong>{' '}
                        {t('goes back to pen')} {penNo(m.from_pen_id)}.</>
                      : <>{t('This is not a move — it is where the fish were put in.')}{' '}
                        {t('Pen')} {penNo(m.pen_id)}{' '}
                        {t('will hold no fish at all, and its counts will belong to nobody.')}</>}
                    <button className="btn ghost sm" style={{ marginLeft: 8 }}
                      onClick={() => setSpor(null)}>{t('Cancel')}</button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* Said once, where the gesture is, and only while there is something to
          drag. An instruction manual on a screen that does not need one is
          worse than nothing; a screen with an invisible gesture is worse
          still. */}
      {canWrite && state.occupancy.length > 0 && !arm && !venter && !armFrom && (
        <p className="faint" style={{ margin: 0, fontSize: 12 }}>
          {t('Tap a fish to pick it up, then tap where it goes. Dragging works too.')}
        </p>
      )}
    </div>
  )
}
